<template>
    <div id="welcome-card" :class="{ 'is-expanded': expanded, 'is-done': done, 'is-shake': shaking }">
        <p class="wc-title">{{ cardTitle }}</p>

        <div class="wc-body" @click="expand">
            <transition-group tag="div" class="wc-fields" name="wc-field">
            <div v-for="field in visibleFields" :key="field.id" class="wc-field" :class="fieldClass(field)">
                <!-- Aciklama/onay metinleri panelde zengin metin: duz basilinca
                     ekranda "<p>metin</p>" gorunuyordu. -->
                <p v-if="field.input === 'note'" class="wc-note" v-html="fieldLabel(field)"></p>

                <label v-else-if="field.input === 'check'" class="wc-check">
                    <input type="checkbox" :disabled="done" v-model="values[field.id]" @change="touch(field)" />
                    <span><span v-html="fieldLabel(field)"></span><i v-if="field.required">*</i></span>
                </label>

                <template v-else>
                    <!-- Etiket bos alanda yer tutucu gibi duruyor, odakta yukari kayiyor. -->
                    <label class="wc-label">{{ fieldLabelText(field) }}<i v-if="field.required">*</i></label>

                    <SelectPhoneInput
                        v-if="field.input === 'phone'"
                        v-model="values[field.id]"
                        :name="fieldLabelText(field)"
                        @input="touch(field)"
                    />

                    <select
                        v-else-if="isSelectLike(field)"
                        :disabled="done"
                        v-model="values[field.id]"
                        @change="field.input === 'city' ? onCityChange(field) : touch(field)"
                    >
                        <option value="">—</option>
                        <option v-for="opt in optionsFor(field)" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                        </option>
                    </select>

                    <input
                        v-else
                        :type="inputType(field)"
                        :disabled="done"
                        v-model="values[field.id]"
                        @focus="onFocus(field)"
                        @blur="onBlur(field)"
                        @keydown.enter.prevent="onEnter"
                    />
                </template>

                <span v-if="errors[field.id]" class="wc-icon is-error" aria-hidden="true">!</span>
                <span v-else-if="isValid(field)" class="wc-icon is-ok" aria-hidden="true">✓</span>
                <span v-if="errors[field.id]" class="wc-error">{{ errorText(field) }}</span>
            </div>
            </transition-group>

            <transition name="wc-btn">
                <button
                    v-if="!readonly"
                    class="wc-submit"
                    :disabled="done"
                    @mouseenter="onHover"
                    @click.stop="submit"
                >
                    {{ done ? $t("message.welcomeCardThanks") : $t("message.welcomeCardSubmit") }}
                </button>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import SelectPhoneInput from "./popup-chat/SelectPhoneInput.vue";
import {
    buildFields,
    pickCompactField,
    validateField,
    buildWelcomePayload,
    buildLoginData,
    plainLabel,
} from "../../utils/welcomeFields";
import { resolvePrefill } from "../../utils/welcomePrefill";
import { publishMessage, SystemMessageTypes } from "../../utils/systemPubSub";
import { socketMessageStream, sendSocketMessage } from "../../utils/socketStream";
import request from "../../utils/request";
import { playOutgoing, playHover } from "../../utils/sounds";
import { getPersistenceData, setPersistenceData } from "../../utils/persistenceStorage";
import { log2 } from "../../utils/log2";

const log = log2("WelcomeCard");

export default {
    name: "WelcomeCard",
    components: { SelectPhoneInput },
    // data.errors, VeeValidate'in ayni isimli computed'i ile cakismasin.
    $_veeValidate: {
        errorBagName: "veeErrors",
    },
    props: {
        // Sohbet acildiktan sonra kart salt-okunur "gonderildi" halinde
        // yeniden ciziliyor; degerler disaridan geliyor.
        initialValues: { type: Object, default: null },
        readonly: { type: Boolean, default: false },
    },
    data() {
        return {
            fields: [],
            values: {},
            errors: {},
            touched: {},
            focused: {},
            expanded: false,
            done: false,
            shaking: false,
            shakeTimer: null,
            // Sehir/ilce listesi socket'ten geliyor (location find-states/find-cities).
            cityOptions: [],
            districtOptions: [],
            cityStateCode: null,
            locationSub: null,
            subjects: [],
        };
    },
    computed: {
        // Onceden secilmis departman varsa alan sorulmuyor.
        departmentEnabled() {
            if (this.preSelectedDepartment) return false;
            return !!this.settings.subject;
        },
        // Katlanmisken tek alan gorunur; Jivo da boyle aciliyor. Gosterilen
        // alan tip onceligine gore secilir (ad > eposta > telefon).
        // Panelde ziyaretci dili icin baslik yazildiysa o, yoksa hazir ceviri.
        cardTitle() {
            const content = (((this.settings || {}).webchat || {}).welcomeFormTitle || {}).languageContent || [];
            const entry = content.find((x) => x && x.languageCode === this.$i18n.locale && String(x.text || "").trim());
            return entry ? String(entry.text).trim() : this.$t("message.welcomeCardTitle");
        },
        visibleFields() {
            if (this.expanded) return this.fields;
            const compact = pickCompactField(this.fields);
            return compact ? [compact] : [];
        },
        ...mapState({
            visitorId: (state) => state.chat.visitorId,
            volume: (state) => state.app.volume,
            languages: (state) => state.language.languages,
            // Canli konusma: lastConversationId degil, kapanmis sohbete yazmayalim.
            conversationId: (state) => state.chat.conversationId,
            site: (state) => state.site.site,
            settings: (state) => state.setting.settings,
            inputs: (state) => state.customLoginForm.inputs,
            preDefinedUserData: (state) => state.chat.preDefinedUserData,
            preSelectedDepartment: (state) => state.chat.preSelectedDepartment,
            contactPropertyData: (state) => state.chat.contactPropertyData,
            soundEnabled: (state) => {
                const n = (state.setting.settings.webchat || {}).notifications;
                return !n || n.sound !== false;
            },
            customLoginFormEnabled: (state) => {
                const enabled = state.setting.settings.customLoginFormEnabled;
                const hasVisible = !!state.customLoginForm.inputs.filter((x) => x.visible).length;
                return enabled && hasVisible;
            },
        }),
    },
    async mounted() {
        if (!this.readonly) {
            this.subjects = await this.fetchSubjects().catch(() => []);
        }
        this.fields = buildFields({
            customLoginFormEnabled: this.customLoginFormEnabled,
            inputs: this.inputs,
            locale: this.$i18n.locale,
            departmentEnabled: this.departmentEnabled,
            hasSubjects: !!this.subjects.length,
        });
        if (this.readonly && this.initialValues) {
            this.values = { ...this.initialValues };
            this.expanded = true;
            this.done = true;
        } else {
            this.applyPrefill();
            this.listenLocation();
        }
        // Kart akisa dusunce kullanicinin elle kaydirmasi gerekmesin.
        this.$emit("grow");
    },
    beforeDestroy() {
        clearTimeout(this.shakeTimer);
        if (this.locationSub) this.locationSub.unsubscribe();
    },
    watch: {
        // SDK sonradan veri set edebiliyor: dokunulmamis alanlar guncellenir.
        preDefinedUserData() {
            this.applyPrefill();
        },
        contactPropertyData() {
            this.applyPrefill();
        },
    },
    methods: {
        applyPrefill() {
            const filled = resolvePrefill({
                fields: this.fields,
                preDefinedUserData: this.preDefinedUserData,
                contactPropertyData: this.contactPropertyData,
                lastLoginData: JSON.parse(getPersistenceData("lastLoginData") || "null") || {},
                lastCustomLogins: JSON.parse(getPersistenceData("lastCustomLogins") || "[]"),
                customLoginVersion: this.settings.customLoginVersion,
                queryParams: this.queryParams(),
            });
            const next = { ...this.values };
            Object.keys(filled).forEach((id) => {
                // Kullanicinin yazdigi ezilmez.
                if (!this.touched[id]) next[id] = filled[id];
            });
            this.values = next;
        },
        queryParams() {
            const out = {};
            new URLSearchParams(window.location.search).forEach((v, k) => (out[k] = v));
            return out;
        },
        expand() {
            this.$emit("touched");
            if (this.done || this.expanded) return;
            // Kart acilirken kabin kaydirma konumu birebir korunuyor: kaynagi
            // ne olursa olsun (JS ya da tarayicinin odak takibi) sayfayi
            // asagi cekmesine izin verilmiyor.
            const kapOnce = document.getElementById("messageContent");
            const kaydirmaOnce = kapOnce ? kapOnce.scrollTop : 0;
            this.expanded = true;
            // Acilma sebebi bildiriliyor: ust bilesen dibe cekmesin.
            this.$emit("grow", "expand");
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (!kapOnce) return;
                    if (kapOnce.scrollTop !== kaydirmaOnce) kapOnce.scrollTop = kaydirmaOnce;
                });
            });
        },
        onHover() {
            if (this.done) return;
            playHover({ volume: this.volume, enabled: this.soundEnabled });
        },
        // Enter kart uzerinde hic dinlenmiyordu: form yalniz butonla gonderiliyordu.
        onEnter() {
            if (this.done) return;
            if (!this.expanded) {
                this.expanded = true;
                return;
            }
            this.submit();
        },
        onFocus(field) {
            // Ust bilesen zaman asimi sayacini iptal etsin: ziyaretci
            // dolduruyorken altindan sohbet acilmamali.
            this.$emit("touched");
            this.expand();
            this.$set(this.focused, field.id, true);
        },
        onBlur(field) {
            this.$set(this.focused, field.id, false);
            this.touch(field);
        },
        touch(field) {
            this.$set(this.touched, field.id, true);
            this.$set(this.errors, field.id, validateField(field, this.values[field.id]));
        },
        isValid(field) {
            if (field.input === "note") return false;
            if (!this.touched[field.id] && !this.values[field.id]) return false;
            return !validateField(field, this.values[field.id]);
        },
        // Etiket yukari kaydi mi? Bos ve odaksizken yer tutucu konumunda duruyor.
        isFloating(field) {
            if (["select", "city", "district", "language"].includes(field.input)) return true;
            if (field.input === "phone") return true;
            return !!this.focused[field.id] || !!this.values[field.id];
        },
        isSelectLike(field) {
            return ["select", "city", "district", "language"].includes(field.input);
        },
        inputType(field) {
            if (field.input === "numeric") return "number";
            if (field.input === "email") return "email";
            return "text";
        },
        fieldLabel(field) {
            return field.labelKey ? this.$t(field.labelKey) : field.label;
        },
        // Satir ici etiket ve attribute'lar duz metin ister; panelin zengin
        // metin cikti isaretlemesi burada oldugu gibi goruntuleniyordu.
        fieldLabelText(field) {
            return plainLabel(this.fieldLabel(field));
        },
        errorText(field) {
            const key = this.errors[field.id];
            return key ? this.$t("message.welcomeError" + key.charAt(0).toUpperCase() + key.slice(1)) : "";
        },
        fieldClass(field) {
            return {
                "has-error": !!this.errors[field.id],
                "is-ok": this.isValid(field),
                "is-float": this.isFloating(field),
                "is-phone": field.input === "phone",
            };
        },
        // Sehir ve ilce secenekleri panelde tanimli degil, socket'ten geliyor.
        async fetchSubjects() {
            const { data } = await request.get("/public/subjects/get-all");
            return data
                .map((sub) => {
                    const match = sub.languageContent.find(
                        (x) => x.languageCode === this.settings.language.customerClientDefault
                    );
                    return { id: sub._id, label: match ? match.name : "" };
                })
                .filter((x) => x.label);
        },
        listenLocation() {
            const needsCity = this.fields.some((f) => f.input === "city" || f.input === "district");
            if (!needsCity) return;
            this.locationSub = socketMessageStream.subscribe(({ cmd, data }) => {
                if (cmd === "find_states_response") this.cityOptions = (data && data.states) || [];
                if (cmd === "find_cities_response") this.districtOptions = (data && data.cities) || [];
            });
            sendSocketMessage("location find-states", { siteId: this.site._id, country: "TR" });
        },
        // Il secilince o ilin ilceleri isteniyor.
        onCityChange(field) {
            this.touch(field);
            const match = (this.cityOptions || []).find((c) => c.name === this.values[field.id]);
            this.cityStateCode = match ? match.state_code : null;
            this.districtOptions = [];
            const districtId = field.id + "__district";
            this.$set(this.values, districtId, "");
            if (this.cityStateCode) {
                sendSocketMessage("location find-cities", {
                    siteId: this.site._id,
                    country: "TR",
                    state: this.cityStateCode,
                });
            }
        },
        // Duz metin listelerini {value,label} cifti yapiyor; departmanda
        // deger ID olmali (atama algoritmasi id bekliyor), etiket degil.
        normalizeOptions(list) {
            return (list || []).filter(Boolean).map((x) => (typeof x === "string" ? { value: x, label: x } : x));
        },
        optionsFor(field) {
            if (field.type === "subject") {
                return (this.subjects || []).filter((x) => x.label).map((x) => ({ value: x.id, label: x.label }));
            }
            if (field.input === "city") return this.normalizeOptions((this.cityOptions || []).map((c) => c.name));
            if (field.input === "district") {
                return this.normalizeOptions((this.districtOptions || []).map((c) => c.name));
            }
            if (field.input === "language") {
                return this.normalizeOptions((this.languages || []).map((l) => l.label || l.name));
            }
            return this.normalizeOptions(field.options);
        },
        // Kilitli cubuga dokunulunca cagriliyor: formu belli edip ilk
        // eksik alana odakliyor, yoksa ziyaretci nereye bakacagini bilmiyor.
        shake() {
            if (this.done) return;
            this.expanded = true;
            this.shaking = false;
            clearTimeout(this.shakeTimer);
            this.$nextTick(() => {
                this.shaking = true;
                this.shakeTimer = setTimeout(() => (this.shaking = false), 500);
                const eksik = this.fields.find(
                    (f) => f.input !== "note" && f.input !== "check" && !this.values[f.id]
                );
                const hedef = this.$el.querySelector(
                    eksik ? ".wc-field:not(.has-value) input, .wc-field:not(.has-value) select" : "input, select"
                );
                if (hedef) hedef.focus();
            });
        },
        submit() {
            const errors = {};
            this.fields.forEach((f) => {
                const err = validateField(f, this.values[f.id]);
                if (err) errors[f.id] = err;
            });
            this.errors = errors;
            if (Object.keys(errors).length) {
                this.expanded = true;
                // Hata halinde dibe cekmiyoruz: kullanici formda yukarida bir
                // alani duzeltiyor olabilir, ekrani altina kacirmak kotu.
                return;
            }

            const payload = buildWelcomePayload({
                fields: this.fields,
                values: this.values,
                inputs: this.inputs,
                base: {
                    visitorId: this.visitorId,
                    siteId: this.site._id,
                    conversationId: this.conversationId,
                    customLoginFormEnabled: this.customLoginFormEnabled,
                },
            });
            if (!!Object.keys(this.contactPropertyData || {}).length) {
                payload.contactPropertyData = this.contactPropertyData;
            }
            const loginData = buildLoginData({
                fields: this.fields,
                values: this.values,
                inputs: this.inputs,
                customLoginFormEnabled: this.customLoginFormEnabled,
            });
            if (!!Object.keys(this.contactPropertyData || {}).length) {
                loginData.contactPropertyData = this.contactPropertyData;
            }
            log("submit", { loginData, payload });
            playOutgoing({ volume: this.volume, enabled: this.soundEnabled });
            this.remember(payload);
            this.done = true;
            // Sohbet bu andan sonra aciliyor: yuk ust bilesene veriliyor ki
            // "visitor login" cagrisi tum veriyle yapilsin.
            this.$emit("submit", { loginData, values: { ...this.values } });
            this.$emit("grow");
        },
        remember(payload) {
            if (this.customLoginFormEnabled) {
                const formVals = (this.inputs || []).reduce((acc, input) => {
                    const match = payload.customLoginFormValues.find((v) => v.input === input._id);
                    acc[input.type] = match ? match.value : null;
                    return acc;
                }, {});
                formVals.customLoginFormValues = payload.customLoginFormValues;
                formVals.version = this.settings.customLoginVersion;
                setPersistenceData("lastCustomLogins", JSON.stringify([formVals]), true);
                return;
            }
            setPersistenceData(
                "lastLoginData",
                JSON.stringify({ fullname: payload.fullname, email: payload.email, phone: payload.phone }),
                true
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/components/v2/WelcomeCard.scss";
</style>



// WEBPACK FOOTER //
// src/components/V2/WelcomeCard.vue